import { useTranslation } from "@sagot/i18n";
import { Button, Icon } from "@sagot/ui";
import { INCOMPLETE_REASON_OF_PROBLEM, problemText } from "../problem-model";
import type { Step } from "../types";
import { useProblems } from "./problems-context";
import { useRefs } from "./refs";
import { STEP_ICONS } from "./step-icons";
import { exitsOf, incompleteOf, summarize, type StepExit } from "./step-summary";

const B = "engage.workflows.builder";

interface StepRowProps {
  step: Step;
  index: number;
  count: number;
  disabled: boolean;
  onEdit: (index: number) => void;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}

function Warning({ text }: { text: string }) {
  return (
    <span className="mt-1 flex items-center gap-1 text-xs font-semibold text-warning">
      <Icon name="alert-triangle" className="size-3.5 shrink-0" />
      {text}
    </span>
  );
}

/** An exit and where it leads; an exit not connected says what happens instead (the next step, the end), or warns while a path is still needed. */
function ExitChip({ exit }: { exit: StepExit }) {
  const { t } = useTranslation();
  const to = "engage.workflows.summaries.exits.to";
  if (exit.to) return <span className="rounded-pill border border-line bg-surface px-2.5 py-1 text-xs font-semibold">{t(to, { label: exit.label, path: exit.to })}</span>;
  const missing = exit.fallback === "unset";
  const instead = t(`engage.workflows.connectors.fallback.${exit.fallback ?? "unset"}`);
  return (
    <span className={missing ? "inline-flex items-center gap-1 rounded-pill border border-warning bg-warning-soft px-2.5 py-1 text-xs font-semibold text-warning" : "rounded-pill border border-dashed border-line bg-surface px-2.5 py-1 text-xs font-semibold text-fg-muted"}>
      {missing ? <Icon name="alert-triangle" className="size-3" /> : null}
      {t(to, { label: exit.label, path: instead })}
    </span>
  );
}

/** One step on a path: what it does, the paths it leads to, what is wrong with it, and move / remove. */
export function StepRow({ step, index, count, disabled, onEdit, onMove, onRemove }: StepRowProps) {
  const { t } = useTranslation();
  const refs = useRefs();
  const exits = exitsOf(step, t, { all: true, lastStep: index === count - 1 });
  const incomplete = incompleteOf(step);
  // What is still missing in a step is told at once (below); the checks of the graph and the server's answers come with the problems.
  const found = useProblems().byStep.get(step.id) ?? [];
  const problems = found.filter((problem) => problem.code !== "incomplete" && INCOMPLETE_REASON_OF_PROBLEM[problem.code] !== incomplete);
  const flagged = found.some((problem) => problem.code !== "incomplete");
  const problemsId = `step-problems-${step.id}`;
  return (
    <li className={flagged ? "rounded-md border-2 border-warning" : "rounded-md border border-line"} data-testid="workflow-step" data-flagged={flagged ? "true" : undefined}>
      <div className="flex items-start gap-3 p-3">
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-sunken text-primary-ink">
          <Icon name={STEP_ICONS[step.type]} className="size-4" />
        </span>
        <button type="button" className="min-w-0 flex-1 text-left" aria-describedby={problems.length > 0 ? problemsId : undefined} onClick={() => { onEdit(index); }}>
          <span className="block text-xs font-bold uppercase tracking-wider text-fg-subtle">{t(`engage.workflows.steps.${step.type}`)}</span>
          <span className="block truncate text-sm">{summarize(step, refs, t)}</span>
          {exits.length > 0 ? (
            <span className="mt-2 flex flex-wrap gap-1.5" data-testid="step-exits">
              {exits.map((exit, position) => (
                <ExitChip key={`${exit.label}-${String(position)}`} exit={exit} />
              ))}
            </span>
          ) : null}
          {step.type === "reply_buttons" && step.allow_typing ? <span className="mt-2 block text-xs text-fg-subtle">{t("engage.workflows.summaries.typedInstead")}</span> : null}
          {problems.length > 0 ? (
            <span id={problemsId} className="block" data-testid="step-problems">
              {[...new Set(problems.map((problem) => problemText(problem, t)))].map((text) => <Warning key={text} text={text} />)}
            </span>
          ) : null}
          {incomplete ? <Warning text={t(`${B}.incomplete.${incomplete}`)} /> : null}
        </button>
        <div className="flex shrink-0 gap-0.5">
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || index === 0} aria-label={t(`${B}.moveStepUp`)} onClick={() => { onMove(index, index - 1); }}>
            <Icon name="chevron-up" />
          </Button>
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || index === count - 1} aria-label={t(`${B}.moveStepDown`)} onClick={() => { onMove(index, index + 1); }}>
            <Icon name="chevron-down" />
          </Button>
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t(`${B}.removeStep`)} onClick={() => { onRemove(index); }}>
            <Icon name="trash" />
          </Button>
        </div>
      </div>
    </li>
  );
}
